import React, { useState } from "react";
import { motion } from "framer-motion";
import { GitCompare, TrendingUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import {
	Radar,
	RadarChart,
	PolarGrid,
	PolarAngleAxis,
	PolarRadiusAxis,
	ResponsiveContainer,
	Legend,
	Tooltip,
} from "recharts";
import { Card, Badge } from "../GraderUI";

const providerColors = {
	openai: "#10b981",
	gemini: "#3b82f6",
	claude: "#8b5cf6",
};

const providerDisplayNames = {
	openai: "OpenAI",
	gemini: "Google Gemini",
	claude: "Anthropic Claude",
};

const getAgreementTextClass = (level) => {
	switch (level) {
		case "high":
			return "provider-comparison__agreement-value--high";
		case "medium":
			return "provider-comparison__agreement-value--medium";
		case "low":
			return "provider-comparison__agreement-value--low";
		default:
			return "provider-comparison__agreement-value--default";
	}
};

const getAgreementCardClass = (level) => {
	switch (level) {
		case "high":
			return "provider-comparison__agreement-card--high";
		case "medium":
			return "provider-comparison__agreement-card--medium";
		case "low":
			return "provider-comparison__agreement-card--low";
		default:
			return "provider-comparison__agreement-card--default";
	}
};

const getAgreementBadgeClass = (level) => {
	switch (level) {
		case "high":
			return "provider-comparison__badge--high";
		case "medium":
			return "provider-comparison__badge--medium";
		case "low":
			return "provider-comparison__badge--low";
		default:
			return "provider-comparison__badge--default";
	}
};

export default function ProviderComparisonSection({ data }) {
	const [expandedCategory, setExpandedCategory] = useState(null);

	if (!data) {
		return null;
	}

	const { category_comparison, overall_agreement_score, agreement_level, providers_evaluated } =
		data;

	const radarData = Object.entries(category_comparison).map(([category, comp]) => {
		const dataPoint = {
			category: category.replace(/_/g, " ").toUpperCase(),
			fullName: category,
		};

		providers_evaluated.forEach((provider) => {
			const score = comp.provider_scores[provider];
			dataPoint[provider] = score !== null ? score : 0;
		});

		return dataPoint;
	});

	return (
		<motion.div
			className="provider-comparison"
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
		>
			<div className="provider-comparison__header">
				<div className="provider-comparison__header-icon-wrap">
					<GitCompare className="provider-comparison__header-icon" />
				</div>
				<div>
					<h2 className="provider-comparison__title">Cross-AI Provider Comparison</h2>
					<p className="provider-comparison__subtitle">
						Side-by-side analysis across {providers_evaluated.length} AI providers
					</p>
				</div>
			</div>

			<Card
				className={`provider-comparison__agreement-card ${getAgreementCardClass(agreement_level)}`}
			>
				<div className="provider-comparison__agreement-row">
					<div>
						<p className="provider-comparison__agreement-label">Provider Agreement Score</p>
						<p
							className={`provider-comparison__agreement-value ${getAgreementTextClass(agreement_level)}`}
						>
							{overall_agreement_score.toFixed(0)}%
							<span className="provider-comparison__agreement-level">
								({agreement_level} agreement)
							</span>
						</p>
					</div>
					<div className="provider-comparison__agreement-icon-wrap">
						{agreement_level === "high" ? (
							<CheckCircle2 className="provider-comparison__agreement-icon provider-comparison__agreement-icon--high" />
						) : agreement_level === "medium" ? (
							<TrendingUp className="provider-comparison__agreement-icon provider-comparison__agreement-icon--medium" />
						) : (
							<AlertTriangle className="provider-comparison__agreement-icon provider-comparison__agreement-icon--low" />
						)}
					</div>
				</div>
			</Card>

			<div className="provider-comparison__chart-section">
				<h3 className="provider-comparison__section-title">Category Score Comparison</h3>
				<div className="provider-comparison__chart-wrap">
					<ResponsiveContainer width="100%" height={400}>
						<RadarChart data={radarData}>
							<PolarGrid />
							<PolarAngleAxis dataKey="category" tick={{ fontSize: 11 }} />
							<PolarRadiusAxis angle={90} domain={[0, 100]} />
							{providers_evaluated.map((provider) => (
								<Radar
									key={provider}
									name={providerDisplayNames[provider] || provider}
									dataKey={provider}
									stroke={providerColors[provider] || "#6b7280"}
									fill={providerColors[provider] || "#6b7280"}
									fillOpacity={0.3}
								/>
							))}
							<Legend />
							<Tooltip />
						</RadarChart>
					</ResponsiveContainer>
				</div>
			</div>

			<div className="provider-comparison__breakdown">
				<h3 className="provider-comparison__section-title">Detailed Category Breakdown</h3>
				<div className="provider-comparison__breakdown-list">
					{Object.entries(category_comparison).map(([category, comp]) => (
						<div key={category} className="provider-comparison__breakdown-item">
							<button
								type="button"
								onClick={() =>
									setExpandedCategory(expandedCategory === category ? null : category)
								}
								className="provider-comparison__breakdown-trigger"
							>
								<div className="provider-comparison__breakdown-trigger-left">
									<span className="provider-comparison__breakdown-name">
										{category.replace(/_/g, " ").toUpperCase()}
									</span>
									<Badge
										className={`provider-comparison__badge ${getAgreementBadgeClass(comp.agreement_level)}`}
									>
										{comp.agreement_level} agreement
									</Badge>
								</div>
								<div className="provider-comparison__breakdown-trigger-right">
									<span className="provider-comparison__breakdown-stats">
										Avg: {comp.average_score.toFixed(1)} | σ:{" "}
										{comp.std_deviation.toFixed(1)}
									</span>
									<span className="provider-comparison__breakdown-chevron">
										{expandedCategory === category ? "▼" : "▶"}
									</span>
								</div>
							</button>

							{expandedCategory === category && (
								<div className="provider-comparison__breakdown-body">
									<div className="provider-comparison__provider-grid">
										{providers_evaluated.map((provider) => (
											<div key={provider} className="provider-comparison__provider-card">
												<div className="provider-comparison__provider-card-header">
													<span
														className="provider-comparison__provider-name"
														style={{ color: providerColors[provider] }}
													>
														{providerDisplayNames[provider] || provider}
													</span>
													<span className="provider-comparison__provider-score">
														{comp.provider_scores[provider] !== null
															? comp.provider_scores[provider]?.toFixed(1)
															: "N/A"}
													</span>
												</div>
												<p className="provider-comparison__provider-reasoning">
													{comp.provider_reasoning[provider] || "No reasoning provided"}
												</p>
											</div>
										))}
									</div>
								</div>
							)}
						</div>
					))}
				</div>
			</div>

			{data.insights_comparison && (
				<div className="provider-comparison__themes">
					{data.insights_comparison.common_strength_themes.length > 0 && (
						<div className="provider-comparison__theme-panel provider-comparison__theme-panel--strength">
							<h4 className="provider-comparison__theme-title">Common Strength Themes</h4>
							<div className="provider-comparison__theme-tags">
								{data.insights_comparison.common_strength_themes.map((theme, idx) => (
									<Badge key={idx} className="provider-comparison__theme-badge provider-comparison__theme-badge--strength">
										{theme}
									</Badge>
								))}
							</div>
						</div>
					)}

					{data.insights_comparison.common_weakness_themes.length > 0 && (
						<div className="provider-comparison__theme-panel provider-comparison__theme-panel--weakness">
							<h4 className="provider-comparison__theme-title">Common Weakness Themes</h4>
							<div className="provider-comparison__theme-tags">
								{data.insights_comparison.common_weakness_themes.map((theme, idx) => (
									<Badge key={idx} className="provider-comparison__theme-badge provider-comparison__theme-badge--weakness">
										{theme}
									</Badge>
								))}
							</div>
						</div>
					)}
				</div>
			)}
		</motion.div>
	);
}
